import { useEffect, useState } from "react"

const OffersPopup = ({
  show,
  offers,
  currentIndex,
  onClose,
  onNext,
  onPrev,
  titleFallback = "Découvrir nos offres",
  descriptionFallback = "Profitez de nos offres exclusives",
}) => {
  const hasOffers = !!offers && offers.length > 0
  const [resolvedImage, setResolvedImage] = useState("/images/offres_popup.jpg")
  const [touchStartX, setTouchStartX] = useState(null)
  const [progress, setProgress] = useState(0)
  const [slideDirection, setSlideDirection] = useState("right")
  const [imageLoaded, setImageLoaded] = useState(false)

  // Fonction pour précharger une image
  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      if (!src) {
        reject(new Error("No source"))
        return
      }
      const img = new Image()
      img.onload = () => resolve(src)
      img.onerror = () => reject(src)
      img.src = src
    })
  }

  useEffect(() => {
    const candidateOffer = hasOffers ? (offers[currentIndex] || offers[0]) : null
    const candidate = candidateOffer?.image
    if (!candidate) {
      setResolvedImage("/images/offres_popup.jpg")
      setImageLoaded(true)
      return
    }
    
    // Réinitialiser l'état de chargement quand on change d'image
    setImageLoaded(false)
    
    let cancelled = false
    const img = new Image()
    img.onload = () => {
      if (!cancelled) {
        setResolvedImage(candidate)
        setImageLoaded(true) // Image chargée avec succès
      }
    }
    img.onerror = () => {
      if (!cancelled) {
        setResolvedImage("/images/offres_popup.jpg")
        setImageLoaded(true) // Même en cas d'erreur, on considère comme "chargé"
      }
    }
    img.src = candidate
    return () => {
      cancelled = true
    }
  }, [hasOffers, offers, currentIndex])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!show || !imageLoaded) return // Ne pas permettre la navigation si l'image n'est pas chargée
      if (e.key === "ArrowRight") {
        if (imageLoaded) {
          setSlideDirection("right")
          setProgress(0)
          onNext && onNext()
        }
      } else if (e.key === "ArrowLeft") {
        if (imageLoaded) {
          setSlideDirection("left")
          setProgress(0)
          onPrev && onPrev()
        }
      } else if (e.key === "Escape") {
        onClose && onClose()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [show, imageLoaded, onNext, onPrev, onClose])

  useEffect(() => {
    if (!show) {
      setProgress(0)
      return
    }
    
    // Ne démarrer le timer que si l'image est chargée
    if (!imageLoaded) {
      setProgress(0)
      return
    }
    
    setProgress(0)
    const rotationMs = 5000 // 5 secondes pour laisser le temps de voir l'image
    const stepMs = 100
    const step = (100 * stepMs) / rotationMs
    let progressId = setInterval(() => {
      setProgress((p) => {
        const np = p + step
        if (np >= 100) {
          // Quand la barre de progression atteint 100%, passer à l'image suivante
          clearInterval(progressId)
          if (hasOffers && offers.length > 1 && imageLoaded) {
            setSlideDirection("right")
            setProgress(0)
            onNext && onNext()
          }
          return 100
        }
        return np
      })
    }, stepMs)
    return () => clearInterval(progressId)
  }, [show, currentIndex, imageLoaded, hasOffers, offers.length, onNext])

  if (!hasOffers) return null

  const currentOffer = offers[currentIndex] || offers[0]
  const hasMultipleOffers = offers.length > 1
  const slideClass = slideDirection === "left" ? "slide-in-left" : "slide-in-right"
  const handleNext = () => {
    // Ne passer à l'image suivante que si l'image actuelle est chargée
    if (!imageLoaded) return
    setSlideDirection("right")
    setProgress(0)
    onNext && onNext()
  }
  const handlePrev = () => {
    // Ne passer à l'image précédente que si l'image actuelle est chargée
    if (!imageLoaded) return
    setSlideDirection("left")
    setProgress(0)
    onPrev && onPrev()
  }

  return (
    <div className={`novotel-v2-popup-overlay ${show ? "active" : ""} offers-popup-overlay ${show ? "" : "hidden"}`}>
      <div className={`novotel-v2-popup offers-popup-modal ${show ? "" : "hidden"}`}>
        <div className="offers-popup-progress">
          <div
            className="offers-popup-progress-bar"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div
          className="novotel-v2-popup-image offers-popup-image-wrapper"
          onTouchStart={(e) => setTouchStartX(e.touches[0]?.clientX ?? null)}
          onTouchEnd={(e) => {
            const endX = e.changedTouches[0]?.clientX ?? null
            if (touchStartX != null && endX != null) {
              const dx = endX - touchStartX
              if (dx > 40) handlePrev()
              else if (dx < -40) handleNext()
            }
            setTouchStartX(null)
          }}
        >
          <div
            className={`offers-popup-image ${slideClass}`}
            style={{ 
              backgroundImage: `url("${resolvedImage}")`,
              opacity: imageLoaded ? 1 : 0.5,
            }}
          >
            {!imageLoaded && (
              <div style={{ 
                position: "absolute", 
                top: "50%", 
                left: "50%", 
                transform: "translate(-50%, -50%)",
                color: "rgba(255,255,255,0.7)",
                fontSize: "16px",
                zIndex: 10
              }}>
                Chargement...
              </div>
            )}
          </div>
          {hasMultipleOffers && (
            <>
              <button
                className="offers-popup-nav-btn prev"
                aria-label="Offre précédente"
                onClick={handlePrev}
              >
                ‹
              </button>
              <button
                className="offers-popup-nav-btn next"
                aria-label="Offre suivante"
                onClick={handleNext}
              >
                ›
              </button>
            </>
          )}
          <button className="novotel-v2-popup-close offers-popup-close-btn" aria-label="Fermer le popup" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="novotel-v2-popup-content-wrapper offers-popup-content">
          <div className="novotel-v2-popup-title offers-popup-title">
            {currentOffer.title || titleFallback}
          </div>
          <div className="novotel-v2-popup-content offers-popup-description">
            {currentOffer.description || descriptionFallback}
          </div>
          <a href="/offres-client#" className="novotel-v2-popup-button offers-popup-cta">
            {titleFallback}
          </a>
          {hasMultipleOffers && (
            <div className="offers-popup-pagination">
              <div className="offers-popup-counter">
                {currentIndex + 1} / {offers.length}
              </div>
              <div className="offers-popup-dots" role="tablist" aria-label="Pagination des offres">
                {offers.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`offers-popup-dot ${idx === currentIndex ? "active" : "inactive"}`}
                    role="tab"
                    aria-label={`Aller à l’offre ${idx + 1}`}
                    aria-selected={idx === currentIndex}
                    onClick={() => {
                      if (idx === currentIndex) return
                      if (idx > currentIndex) handleNext()
                      else handlePrev()
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OffersPopup
